// The name rules of a Data Storage file. The server checks the same ones
// (backend/app/jobs/storage_file_rename_plan.py); these only keep the dialogs
// from sending a name it would refuse.

export interface FileNameParts {
  base: string;
  extension: string;
}

const EXTENSION = /^[A-Za-z0-9]{1,16}$/;
// What Windows refuses in a file name, plus the brackets: a renamed file can be
// saved under the name it shows.
const RENAME_REFUSED = new Set([
  "<",
  ">",
  ":",
  '"',
  "/",
  "\\",
  "|",
  "?",
  "*",
  "[",
  "]",
]);
export const MAX_FILE_NAME_LENGTH = 255;

/** The name split into what a rename may change and the extension it keeps. */
export const splitFileName = (name: string): FileNameParts => {
  const dot = name.lastIndexOf(".");
  if (dot > 0 && dot < name.length - 1 && EXTENSION.test(name.slice(dot + 1))) {
    return { base: name.slice(0, dot), extension: name.slice(dot) };
  }
  return { base: name, extension: "" };
};

/** The name a typed base gives the file: trimmed, inner spaces collapsed. */
export const renamedFileName = (base: string, extension: string): string =>
  base.normalize("NFC").split(/\s+/).filter(Boolean).join(" ") + extension;

const isControl = (ch: string) => {
  const code = ch.codePointAt(0) ?? 0;
  return code < 32 || code === 127;
};

const utf8Length = (name: string) =>
  Array.from(name).reduce((total, ch) => {
    const code = ch.codePointAt(0) ?? 0;
    return (
      total + (code < 0x80 ? 1 : code < 0x800 ? 2 : code < 0x10000 ? 3 : 4)
    );
  }, 0);

/** Why storage cannot hold this name at all (an upload checks this much), or null. */
export const storedFileNameProblem = (name: string): string | null => {
  if (Array.from(name).some(isControl)) {
    return "A file name cannot contain control characters.";
  }
  if (
    Array.from(name).length > MAX_FILE_NAME_LENGTH ||
    utf8Length(name) > MAX_FILE_NAME_LENGTH
  ) {
    return `A file name can be at most ${MAX_FILE_NAME_LENGTH} characters.`;
  }
  return null;
};

/** Why a renamed file cannot take this name, or null. */
export const renameProblem = (
  base: string,
  extension: string,
): string | null => {
  const name = renamedFileName(base, extension);
  if (name.length === extension.length) return "Enter a file name.";
  if (name === "." || name === "..") return "Invalid file name.";
  const refused = Array.from(
    new Set(Array.from(name).filter((ch) => RENAME_REFUSED.has(ch))),
  );
  if (refused.length) return `A file name cannot contain ${refused.join(" ")}`;
  return storedFileNameProblem(name);
};
